package pl.nickers.match3.ecs

import kotlin.random.Random

/**
 * A spawn-pool entry derived from a [PieceDefinition]. Internal to [EntityCatalog].
 */
private data class SpawnableEntry(
    val pieceId: String,
    val family: Family,
    val weight: Double,
    val body: String,
    val faces: List<String>,
)

/**
 * A selected entity ready to be turned into ECS components.
 *
 * [pieceId] is the canonical piece-set id (added in Phase 2). [typeId] is
 * the legacy integer id used by match detection (`0` for bombs, `1+` for
 * jellies). Both are populated; callers can use whichever fits their need.
 */
data class EntitySelection(
    val type: String,
    val bodyImage: String,
    val faceImage: String,
    val typeId: Int,
    val pieceId: String,
)

/**
 * Catalog of spawnable entity templates, derived from a [PieceSet].
 *
 * Phase 3 of the data-driven refactor: the catalog no longer reads
 * `entity_config.json`; it now wraps a [PieceSet] and exposes the same
 * legacy API (`bodyTypeMap`, `jellyBodyImages`, `typeIdForBody`,
 * `selectRandom`) plus the new [EntitySelection.pieceId]. Legacy callers
 * continue to work unchanged while moving toward piece-id-based
 * dispatch in later phases.
 *
 * Assigns stable integer type IDs to each unique body image so that the
 * match-detection system can still compare by Int. Bombs receive
 * typeId 0; jellies are numbered 1..N in piece-set order.
 *
 * Usage:
 *   val catalog = EntityCatalog.load()       // suspend, reads piece set
 *   val selection = catalog.selectRandom(random)
 */
class EntityCatalog(val pieceSet: PieceSet) {

    private val spawnable: List<SpawnableEntry> = pieceSet.spawnable.map { (pieceId, def) ->
        SpawnableEntry(
            pieceId = pieceId,
            family = def.family,
            weight = def.spawnWeight,
            body = def.sprites.body
                ?: error("Spawnable piece '$pieceId' must define sprites.body"),
            faces = def.sprites.faces,
        )
    }

    private val totalWeight: Double = spawnable.sumOf { it.weight }

    /** Maps body image filename → integer type ID (0 = bomb, 1+ = jelly). */
    val bodyTypeMap: Map<String, Int>

    /** All unique body image filenames for jelly-type entries, in piece-set order. */
    val jellyBodyImages: List<String>

    init {
        require(spawnable.isNotEmpty()) { "EntityCatalog needs at least one spawnable piece" }
        val map = mutableMapOf<String, Int>()
        val jellyList = mutableListOf<String>()
        var nextId = 1
        for (entry in spawnable) {
            if (entry.body in map) continue
            if (entry.family == Family.BOMB) {
                map[entry.body] = 0
            } else {
                map[entry.body] = nextId++
                jellyList.add(entry.body)
            }
        }
        bodyTypeMap = map
        jellyBodyImages = jellyList
    }

    /** Returns the integer type ID for a given body image filename. */
    fun typeIdForBody(bodyImage: String): Int = bodyTypeMap[bodyImage] ?: 1

    /**
     * Picks a random spawnable piece (weighted by `spawnWeight`), then picks
     * a random face from its sprite pool. The returned [EntitySelection]
     * carries both the legacy [typeId] and the new [pieceId].
     */
    fun selectRandom(random: Random): EntitySelection {
        val entry = weightedSelect(random)
        val face = if (entry.faces.isNotEmpty()) entry.faces.random(random) else "face_1.png"
        val typeLabel = if (entry.family == Family.BOMB) "bomb" else "jelly"
        return EntitySelection(
            type = typeLabel,
            bodyImage = entry.body,
            faceImage = face,
            typeId = typeIdForBody(entry.body),
            pieceId = entry.pieceId,
        )
    }

    private fun weightedSelect(random: Random): SpawnableEntry {
        var remaining = random.nextDouble() * totalWeight
        for (entry in spawnable) {
            remaining -= entry.weight
            if (remaining <= 0.0) return entry
        }
        return spawnable.last()
    }

    companion object {
        /** Loads the active piece set from compose resources and wraps it. */
        suspend fun load(): EntityCatalog = EntityCatalog(PieceSetLoader.load())

        /** Synchronous fallback used by tests and pre-async initialisation. */
        fun default(): EntityCatalog = EntityCatalog(PieceSetLoader.default())
    }
}
